import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const FeesTrendChart = ({ data, loading }) => {
  const mockData = [
    { month: 'Jan 2024', totalFees: 450000, installations: 12, brands: 3 },
    { month: 'Feb 2024', totalFees: 520000, installations: 15, brands: 4 },
    { month: 'Mar 2024', totalFees: 380000, installations: 10, brands: 3 },
    { month: 'Apr 2024', totalFees: 620000, installations: 18, brands: 5 },
    { month: 'May 2024', totalFees: 580000, installations: 16, brands: 4 },
    { month: 'Jun 2024', totalFees: 720000, installations: 22, brands: 6 },
    { month: 'Jul 2024', totalFees: 680000, installations: 20, brands: 5 },
    { month: 'Aug 2024', totalFees: 750000, installations: 24, brands: 6 },
    { month: 'Sep 2024', totalFees: 820000, installations: 26, brands: 7 }
  ];

  const chartData = data || mockData;

  const formatCurrency = (value) => {
    return `৳${(value / 1000)?.toFixed(0)}K`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-surface border border-border rounded-lg p-3 shadow-elevated">
          <p className="font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-muted-foreground">{entry?.dataKey === 'totalFees' ? 'Total Fees:' : 'Installations:'}</span>
              <span className="font-medium text-foreground">
                {entry?.dataKey === 'totalFees' ? formatCurrency(entry?.value) : entry?.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-80">
          <div className="flex items-center gap-3 text-muted-foreground">
            <div className="animate-spin">
              <Icon name="Loader2" size={20} />
            </div>
            <span>Loading chart data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Revenue Trend Analysis</h3>
          <p className="text-sm text-muted-foreground">Monthly branding fees and installation counts</p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-primary rounded-full" />
            <span className="text-muted-foreground">Total Fees</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-accent rounded-full" />
            <span className="text-muted-foreground">Installations</span>
          </div>
        </div>
      </div>

      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="month" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              yAxisId="fees"
              orientation="left"
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickFormatter={formatCurrency}
            />
            <YAxis 
              yAxisId="count"
              orientation="right"
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              yAxisId="fees"
              type="monotone"
              dataKey="totalFees"
              stroke="var(--color-primary)"
              strokeWidth={3}
              dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: 'var(--color-primary)', strokeWidth: 2 }}
            />
            <Line
              yAxisId="count"
              type="monotone"
              dataKey="installations"
              stroke="var(--color-accent)"
              strokeWidth={2}
              dot={{ fill: 'var(--color-accent)', strokeWidth: 2, r: 3 }}
              activeDot={{ r: 5, stroke: 'var(--color-accent)', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default FeesTrendChart;